// MIGRAÇÃO DE LIMPEZA dos carimbos do auto-advance em cascata (decisão do dono,
// 27/09/2026: "opção (a), só os carimbados").
//
// O defeito (consertado nesta mesma versão — ver
// docs/changelog/tv-series-autoadvance-gravador-em-lote.md) marcou como assistidos
// vários episódios seguintes, com o pos/dur do episódio que terminou. Esta migração
// apaga SÓ os registros que batem na assinatura (`lib/autoadvance-stamp.ts`) e avisa
// o servidor com tombstone, pra os outros aparelhos da conta também limparem.
//
// ⚠️ ORDEM OBRIGATÓRIA: ela viaja no MESMO pacote que o conserto da cascata. Rodando
// numa versão com a cascata, o próximo auto-advance recarimbaria tudo.
//
// ── Uma vez por aparelho, e idempotente ──
// Estado em localStorage (a cota do IndexedDB oscila em zero na TV do dono; o
// localStorage é cota separada). Duas metades independentes:
//   - `localDone`: a limpeza local terminou (varredura completa + todos os deletes
//     aplicados). Depois disso não se varre mais.
//   - `pending`: tombstones que ainda não entraram no outbox. São GRAVADOS ANTES do
//     delete local — se o app morrer no meio, o que falta avisar não se perde (depois
//     do delete, a varredura já não os acharia). Tentados de novo a cada boot até o
//     outbox aceitar.
// Rodar duas vezes dá o mesmo resultado que uma: a segunda varredura não acha mais
// carimbo (de cada lote sobra só o segundo mais antigo, que a regra de empate mantém
// inteiro — ver "QUAL FICA" em lib/autoadvance-stamp.ts), e tombstone repetido é
// inofensivo (o servidor fica com o `deleted_at` mais novo). Se a própria marca
// falhar ao gravar, a migração só roda de novo — sem efeito extra.
//
// ── Por que o tombstone importa (e por que o status) ──
// O outbox DESCARTA o write quando o IDB recusa (quota) e antes nem avisava. Um
// tombstone descartado deixa o servidor com o carimbo: o outro aparelho continua
// carimbado, e um pull completo (reinstalar, novo pareamento) devolveria o carimbo
// aqui. Por isso `enqueueDelete` agora devolve o desfecho e esta migração só dá o
// tombstone por entregue quando ele está no outbox.
//
// O que ela NUNCA faz: apagar série inteira, mexer em série sem lote detectado, ou
// apagar o registro mantido de um lote (o do episódio que terminou de verdade).
//
// ── A QUEM o tombstone pode ser mandado (gate de cybersec, 27/09/2026) ──
// A varredura local pega os perfis de TODAS as contas que já passaram pela TV (o
// sign-out preserva o dado local de propósito). O apagamento LOCAL vale pra todos.
// O tombstone, não: ele sai pro servidor com o token da sessão ATUAL. Mandar o de
// um perfil da conta A com o token de B (1) travava o sync de B (o flush abortava
// no 403 `profile_not_owned`) e (2) se o perfil de A não existisse no servidor, o
// provisionamento do flush o CRIAVA na conta de B, com o nome de A e itemKeys que
// revelam o que A assistiu. Por isso cada pendente guarda o DONO (a conta Swipe no
// momento em que foi registrado) e só sai quando:
//   - o dono é a conta da sessão atual; e
//   - o perfil aparece em `GET /v1/swipe-account/profiles` dessa sessão.
// Sem dono (sem sessão ao registrar) → nunca sai. Perfil fora da lista → descartado
// (não provisiona).
//
// O que isso faz, na prática, com os carimbos de OUTRA conta: o dono gravado é a
// sessão DA VARREDURA, e a varredura pega os perfis de todas as contas. Um carimbo de
// um perfil da conta A varrido com B logada ganha B como dono, não aparece nos perfis
// de B e é descartado como `not-my-profile`. Ou seja: o servidor da conta A NÃO é
// limpo por este aparelho (só o dado local dela). O `not-owner` (esperar a conta
// voltar) só vale pro pendente registrado numa sessão e reenviado noutra; uma
// re-varredura noutra sessão não troca o dono dele (os dois ficam, ver `mergePending`).
// Limite aceito e registrado: a TV não sabe a que conta pertence um perfil local.
//
// ── O empate do primeiro segundo: a duração real do episódio ──
// Quando a origem divide o segundo mais antigo com carimbos (ver "QUAL FICA" em
// lib/autoadvance-stamp.ts), quem desempata é a duração real dos episódios, do
// `series_info`, e SÓ do provedor ativo (é a sessão dele que responde o catálogo).
// Fonte, da mais barata pra mais cara: o `SeriesInfoStore` da própria TV (IDB local,
// o que a ficha já abriu; vale pra qualquer lote, até o sem empate) e, só pras séries
// com empate PENDENTE, o `series_info` pela API (o caminho normal da ficha, com cache
// no servidor; nunca o Xtream direto), com prioridade baixa (`runLowPriority`) e depois
// do pull. Sem a resposta, o empate fica PENDENTE:
// `localDone` não fecha, e o boot seguinte varre de novo e tenta de novo. Varrer de
// novo, em vez de guardar os ids, revalida contra o dado de agora: se o usuário
// assistiu um episódio empatado entre um boot e outro, o registro mudou e não é mais
// carimbo. Terminação: conta TENTATIVA todo boot em que a resposta é "não dá": o
// catálogo respondeu sem durações, respondeu 4xx (série removida: 404), ou o provedor
// do empate não é o ativo (outro provedor, ou nenhum logado: sem sessão pra perguntar).
// Na STAMP_TIE_MAX_TRIES-ésima, o empate fecha conservador (ficam todos) e a varredura
// para. Só falha de REDE (sem resposta, 5xx, 408/429) não conta: é a única que tenta
// pra sempre, com uma busca por boot, só dessas séries. Antes, 4xx contava como rede e
// o empate de outro provedor nunca contava: a varredura inteira do IDB rodava a cada
// boot, pra sempre (review de 28/09/2026, sonda Q4).
//
// ── Teto GLOBAL (review de 28/09/2026, sonda Q6) ──
// Ainda sobravam três casos sem fim: o catálogo respondendo 5xx pra sempre (dado sujo
// do Xtream numa série), o IndexedDB que não lê (rejeita OU pendura) e o delete do IDB
// que falha sempre (rejeita OU pendura). Custo de
// cada um: um pull e a varredura inteira do IDB a cada boot. A limpeza local tem um
// teto global de boots NÃO concluídos: no teto, fecha conservador (o que ficou, fica:
// carimbos não apagados e empates pendentes viram ✓ a mais, que também podem adiantar o
// "Continuar": ver o fim do cabeçalho de lib/autoadvance-stamp.ts). Ver
// STAMP_GIVE_UP_MIN_BOOTS / STAMP_GIVE_UP_MIN_MS / STAMP_GIVE_UP_HARD_BOOTS. Os
// tombstones já registrados seguem na lista deles (reenvio sem varredura).
//   - O contador é WRITE-AHEAD: gravado já incrementado ANTES do trabalho que pode
//     travar (pull, varredura, busca, deletes). Um boot que pendura no meio (delete do
//     IDB que nunca volta, TV desligada) conta mesmo assim. E o teto é conferido também
//     no INÍCIO do boot: se os anteriores chegaram a ele sem concluir (penduraram), este
//     fecha sem refazer o trabalho. Antes, o `writeState` do passo 1 regravava o estado
//     SEM os contadores, e todo boot que não chegava ao fim os zerava (sonda SA do
//     review, 28/09/2026: delete pendurado + 5xx, 130 boots em 130 dias, sem teto).
//   - Relógio antes do lançamento desta migração (STAMP_MIGRATION_EPOCH_MS) é relógio
//     ERRADO (TV sem RTC antes do NTP, em 1970): a fase local NEM RODA nesse boot (não
//     conta boot, não abre a janela de dias, não varre). Antes, o `since` de 1970 fazia a
//     janela "passar" no instante em que o NTP acertava, e fechava em ~20 boots (sonda SC).
//   - O teto DEPENDE de o estado persistir no localStorage: sem gravar, não há contador
//     nem teto (como já valia pro `localDone`). A sobrevivência da chave
//     `swipe:mig:autoadvance-stamps:v1` a um reboot está no smoke da LG.
//
// ── O `series_progress` é DERIVADO: recalcula e reescreve com o relógio de AGORA ──
// Medido no servidor real (28/09/2026): dos 96 tombstones, os 2 ignorados eram os
// `series_progress` do GoT e do Expresso. O servidor tinha os dois com `updated_at` de
// 28/09 (recalculados DEPOIS do carimbo, mas com os carimbos ainda presentes: Expresso
// apontando o T4E10 onde a cadeia parou, GoT o T2E8 de um carimbo apagado). Tombstone
// ancorado no carimbo nunca vence um derivado recalculado depois, e um pull completo
// traria o ponteiro contaminado de volta. Por isso, nas séries em que a migração apaga
// posições, o `series_progress` é RECALCULADO localmente a partir das posições já limpas
// (o ponteiro do próximo só se o catálogo estiver à mão: o cache local da ficha ou a
// busca do empate; sem ele, o registro sai sem `next*`, o "ainda não calculado" que a
// ficha preenche ao abrir) e sai como ESCRITA com o relógio de agora. Só se não sobrou
// nada assistido, tombstone, também com o relógio de agora. A escrita passa pelo MESMO
// portão de dono e de perfil dos tombstones (é o token da sessão atual que envia).
// A escrita corrige o servidor e o próximo pull completo.
//
// ── O derivado de posições que OUTRO aparelho apagou: recalcula só LOCAL ──
// Os tombstones do 1º aparelho a migrar chegam pelo pull aos outros ANTES da migração
// deles (a ordem provável: o web recebe a versão nova antes da TV). O
// `applyRemoteSyncItem` apaga a posição sem LWW, a varredura daquele aparelho não acha
// mais carimbo nenhum, e o derivado LOCAL dele continuava contaminado (sonda OUTRO do
// review, 29/09/2026: ponteiro no T4E11 do Expresso). A escrita do derivado limpo que o
// 1º aparelho mandou não resolve: no pull, o LWW de CONTEÚDO (`lastWatchedAt`) mantém o
// local, que é mais novo. Por isso, na varredura, todo `series_progress` cujo
// `lastWatchedAt` é MAIOR que o maior `lastWatchedAt` entre as posições `finished` que
// ainda existem da série é RECALCULADO (o mesmo núcleo, com o catálogo do cache local se
// houver) ou apagado se não sobrou posição. É o sinal de um derivado calculado com
// posições que não existem mais, e não depende de ainda haver carimbo. Só LOCAL: o
// servidor já fica certo pelo aparelho que achou os carimbos.
// Quem mais dispara o sinal, e por que recalcular também é o certo pra eles: o episódio
// desmarcado na gaveta de episódios (apaga a posição sem recalcular o snapshot; a ficha
// recalcula ao abrir) e o "Limpar histórico" das Configurações (apaga as posições e deixa
// os snapshots). Nos dois o recálculo dá o que a ficha daria, exceto o ponteiro quando o
// catálogo não está no cache local (a ficha completa ao abrir). No do histórico, o
// snapshot sem posição nenhuma é apagado local, de uma vez só (a migração roda uma vez).
//
// ── O relógio do derivado: o da VARREDURA, com piso e teto (sonda W1, 29/09/2026) ──
// O derivado sai com o relógio do boot que o recalculou, e esse relógio mente: em 1970
// (TV sem RTC antes do NTP) a escrita perdia calado no LWW; em 2037 ela travaria a linha
// no servidor contra toda escrita legítima até lá (o servidor não tem teto: AP-115).
//   - Relógio de AGORA antes do piso (STAMP_MIGRATION_EPOCH_MS): a fase local não roda
//     (ver o teto global), então nenhum derivado nasce com ele; o boot seguinte recalcula.
//   - No reenvio, o pendente leva o relógio da VARREDURA que o calculou, nunca o do
//     reenvio: um "agora" posterior faria o conteúdo velho vencer a escrita legítima que
//     outro aparelho fez entre as duas.
//   - Antes de enfileirar, o relógio do derivado passa pelo mesmo `isAcceptableClock` do
//     `enqueueUpsert` (sync/clock.ts): com o relógio de agora abaixo do piso, espera
//     (`clock-behind`); com o do pendente À FRENTE do de agora (a varredura rodou com o
//     relógio adiantado, o envio falhou e o relógio foi acertado depois), o pendente é
//     descartado e a série volta pra varredura seguinte, que o RECALCULA com o relógio
//     certo (`clock-ahead`, estado `recalc`). Esperar o relógio alcançar o do pendente
//     seria nunca terminar.
//   - O que o cliente NÃO pega: a varredura que roda com o relógio adiantado e envia no
//     mesmo boot (o teto é o próprio relógio adiantado). Isso só o servidor resolve
//     (AP-115).
//   - O tombstone de POSIÇÃO não passa por isso: o relógio dele é o do carimbo (abaixo),
//     do passado por construção. Se o próprio carimbo foi gravado com um relógio
//     adiantado, a linha dele no servidor já tem esse relógio e só um tombstone igual a
//     vence; recusá-lo deixaria o pendente preso pra sempre.
//
// ── O relógio do tombstone: o do CARIMBO, não o de agora ──
// Tombstone com "agora" vence no LWW do servidor QUALQUER escrita — inclusive uma
// legítima feita em outro aparelho depois que a TV puxou pela última vez (e quem
// recebe um tombstone apaga sem comparar data: `applyRemoteSyncItem`). O tombstone
// leva o fim da rajada + TOMBSTONE_ANCHOR_MARGIN_MS: vence a escrita do próprio
// carimbo e PERDE pra escrita legítima feita depois dessa margem, com ou sem pull
// (a que cair DENTRO da margem perde pro tombstone: janela aceita, ver a derivação
// em TOMBSTONE_ANCHOR_MARGIN_MS). E a
// varredura roda depois do primeiro pull da sessão (o do perfil atual), pra não
// apagar local o que o servidor já tem mais novo.

import { api } from './api'
import {
  episodeDurationsFrom,
  findAutoAdvanceStamps,
  type EpisodeDurations,
  type StampGroup,
} from './lib/autoadvance-stamp'
import {
  buildSeriesProgress,
  deletePositionsLocalOnly,
  deleteSeriesProgressLocalOnly,
  putSeriesProgressLocalOnly,
  readAllPositionsAllScopes,
  readAllSeriesProgressAllScopes,
  readFallbackPositionsAllScopes,
  readSeriesProgressLocal,
  seriesProgressEpisodes,
  seriesProgressLocalId,
  type PlaybackPosition,
  type SeriesProgress,
} from './local-db'
import { runLowPriority } from './net-priority'
import { track } from './qoe'
import { providerPart } from './scope'
import { seriesInfoStore } from './series-info-store'
import { enqueueDelete, enqueueUpsert, getSwipeUserId, syncPull, type TombstoneStatus } from './sync'
import { isAcceptableClock, SYNC_CLOCK_MIN_MS } from './sync/clock'
import { listRemoteProfiles } from './sync/sync-api'
import { decomposeLocalId } from './sync/sync-scope'

export const STAMP_MIGRATION_KEY = 'swipe:mig:autoadvance-stamps:v1'

/**
 * Folga do relógio do tombstone depois do fim da rajada.
 *
 * Derivação: o registro do carimbo no servidor tem `updated_at` = momento em que a
 * TV ENFILEIROU a escrita, que vem depois do `lastWatchedAt` (segundos, calculado
 * no cleanup): a cascata inteira roda num flush síncrono (até ~7s medidos no GoT) e
 * só então as escritas de IDB e o enqueue acontecem. 60s cobre essa distância com
 * folga. Uma escrita legítima do MESMO episódio noutro aparelho dentro de 60s depois
 * da cascata (o usuário estava na TV) é o único caso que perde — aceito.
 */
export const TOMBSTONE_ANCHOR_MARGIN_MS = 60_000

/**
 * Quantos boots um empate espera com resposta "não dá" (catálogo sem `duration_secs`,
 * 4xx, ou provedor do empate inativo) antes de fechar conservador (ficam todos os
 * empatados). Derivação: o dado de catálogo muda no ritmo do provedor, não do boot, e
 * a troca de provedor na TV é rara; 3 respostas "não dá" seguidas dizem que o dado não
 * vem. Falha de REDE não conta (não é resposta).
 */
export const STAMP_TIE_MAX_TRIES = 3

/**
 * Teto global da limpeza local (ver "Teto GLOBAL" no cabeçalho). Fecha conservador
 * quando (boots NÃO concluídos ≥ MIN_BOOTS E tempo desde o primeiro deles ≥ MIN_MS) OU
 * boots ≥ HARD_BOOTS. Derivação:
 *   - MIN_MS = 14 dias: o dobro de uma semana de rede fora. Uma queda longa (ou a TV
 *     fora da tomada uma semana) nunca fecha cedo, por mais boots que aconteçam nela;
 *   - MIN_BOOTS = 20: mesmo depois dos 14 dias, exige 20 tentativas de verdade. Uma TV
 *     ligada 1× por semana não fecha na 3ª tentativa só porque o calendário andou;
 *   - HARD_BOOTS = 120: o teto que NÃO depende do relógio (relógio de TV mente: sem
 *     NTP, resetado, voltando no tempo). Numa TV usada 10× por dia, uma semana de rede
 *     fora são ~70 boots, abaixo dele; numa usada 1× por dia, são 4 meses.
 */
export const STAMP_GIVE_UP_MIN_BOOTS = 20
export const STAMP_GIVE_UP_MIN_MS = 14 * 24 * 60 * 60 * 1000
export const STAMP_GIVE_UP_HARD_BOOTS = 120

/**
 * Nenhuma TV roda esta migração antes de ela existir: o dia em que ela foi escrita
 * (27/09/2026, a decisão do dono). Um relógio anterior a isto é relógio ERRADO (TV sem
 * RTC antes do NTP): a fase local não roda com ele. É o MESMO piso que o
 * `enqueueUpsert` exige de um relógio explícito (`SYNC_CLOCK_MIN_MS`, sync/clock.ts):
 * uma fonte só, pra o derivado que a varredura aceita ser o que o outbox aceita.
 */
export const STAMP_MIGRATION_EPOCH_MS = SYNC_CLOCK_MIN_MS

type TombstoneKind = 'position' | 'series_progress'

/** Pendente de sync: tombstone (sem `payload`) ou ESCRITA (com `payload`: o
 *  `series_progress` recalculado). O nome ficou do tempo em que só havia tombstone. */
interface PendingTombstone {
  localId: string
  kind: TombstoneKind
  /** Conta Swipe no momento do registro. null = não havia sessão → nunca sai. */
  owner: string | null
  /** Relógio LWW (ISO): ancorado no carimbo pras posições; agora pro derivado. */
  updatedAt: string
  /** Presente = escrita (upsert) deste conteúdo, não tombstone. */
  payload?: unknown
}

interface MigrationState {
  v: 1
  localDone: boolean
  pending: PendingTombstone[]
  /** Boots com resposta "não dá" por empate (`scope|serie`); ver STAMP_TIE_MAX_TRIES. */
  tieTries?: Record<string, number>
  /** Limpeza local NÃO concluída: desde quando (ms) e quantos boots (teto global). */
  unfinished?: { since: number; boots: number }
  /** Séries cujo derivado a próxima varredura RECALCULA: o pendente dele tinha o relógio
   *  à frente do de agora (ver "O relógio do derivado"). */
  recalc?: Array<{ scope: string; seriesId: string }>
}

/** Desfecho de um pendente nesta rodada. */
export type TombstoneOutcome =
  | TombstoneStatus
  /** Registrado sem sessão: nunca sai (descartado). */
  | 'no-owner'
  /** Registrado noutra sessão (outra conta): espera aquela conta voltar. */
  | 'not-owner'
  /** A lista de perfis da sessão não veio (rede): tenta no próximo boot. */
  | 'profiles-unavailable'
  /** O perfil não é da conta da sessão: descartado (nunca provisiona). */
  | 'not-my-profile'
  /** Derivado com o relógio de AGORA antes do piso: espera o boot seguinte. */
  | 'clock-behind'
  /** Derivado com o relógio À FRENTE do de agora: descartado, a série é recalculada. */
  | 'clock-ahead'

export interface StampMigrationResult {
  /** Nada a fazer: limpeza local feita e nenhum tombstone pendente. */
  skipped: boolean
  /** Grupos com empate que ficaram PENDENTES nesta rodada (falta a duração real). */
  pendingTies: number
  /** A limpeza local fechou pelo TETO GLOBAL nesta rodada (conservador). */
  gaveUp: boolean
  /** A varredura leu o IDB (false = só o fallback; a limpeza local fica pra depois). */
  scanComplete: boolean
  /** O relógio deste boot está antes do piso (STAMP_MIGRATION_EPOCH_MS): a fase local não
   *  rodou. */
  clockBehind: boolean
  groups: StampGroup[]
  removedPositions: string[]
  removedSeriesProgress: string[]
  /** `series_progress` RECALCULADOS e regravados (o derivado limpo, ver o cabeçalho). */
  rewrittenSeriesProgress: string[]
  /** `series_progress` mais novos que as posições que sobraram, recalculados (ou apagados)
   *  SÓ local (ver "O derivado de posições que OUTRO aparelho apagou"). */
  localOnlySeriesProgress: string[]
  /** Desfecho de cada pendente tratado nesta rodada. */
  tombstones: Array<PendingTombstone & { outcome: TombstoneOutcome }>
  state: MigrationState
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function isPending(v: unknown): v is PendingTombstone {
  if (!v || typeof v !== 'object') return false
  const p = v as Record<string, unknown>
  return (
    typeof p['localId'] === 'string' &&
    (p['kind'] === 'position' || p['kind'] === 'series_progress') &&
    (p['owner'] === null || typeof p['owner'] === 'string') &&
    typeof p['updatedAt'] === 'string'
  )
}

function isSeriesRef(v: unknown): v is { scope: string; seriesId: string } {
  if (!v || typeof v !== 'object') return false
  const r = v as Record<string, unknown>
  return typeof r['scope'] === 'string' && typeof r['seriesId'] === 'string'
}

export function readStampMigrationState(): MigrationState {
  const empty: MigrationState = { v: 1, localDone: false, pending: [] }
  const s = storage()
  if (!s) return empty
  try {
    const raw = s.getItem(STAMP_MIGRATION_KEY)
    if (!raw) return empty
    const parsed = JSON.parse(raw) as Partial<MigrationState>
    const tieTries: Record<string, number> = {}
    if (parsed.tieTries && typeof parsed.tieTries === 'object') {
      for (const [k, v] of Object.entries(parsed.tieTries)) if (typeof v === 'number') tieTries[k] = v
    }
    const u = parsed.unfinished
    const unfinished =
      u && typeof u === 'object' && Number.isFinite(u.since) && Number.isFinite(u.boots)
        ? { since: u.since, boots: u.boots }
        : null
    const recalc = Array.isArray(parsed.recalc) ? parsed.recalc.filter(isSeriesRef) : []
    return {
      v: 1,
      localDone: parsed.localDone === true,
      pending: Array.isArray(parsed.pending) ? parsed.pending.filter(isPending) : [],
      ...(Object.keys(tieTries).length > 0 ? { tieTries } : {}),
      ...(unfinished !== null ? { unfinished } : {}),
      ...(recalc.length > 0 ? { recalc } : {}),
    }
  } catch {
    return empty
  }
}

function writeState(state: MigrationState): void {
  const s = storage()
  if (!s) return
  try {
    s.setItem(STAMP_MIGRATION_KEY, JSON.stringify(state))
  } catch {
    // localStorage cheio: a migração roda de novo no próximo boot (idempotente).
  }
}

function mergePending(a: ReadonlyArray<PendingTombstone>, b: ReadonlyArray<PendingTombstone>): PendingTombstone[] {
  // O derivado (`series_progress`) recalculado agora SUBSTITUI o pendente anterior do
  // mesmo id E DO MESMO DONO (é o estado de agora); tombstone de posição repetido é o
  // mesmo, fica o 1º. De dono DIFERENTE, os dois ficam: cada um só sai na sessão do seu
  // dono. Antes a chave não tinha o dono, e a re-varredura sob B trocava o dono do
  // derivado de A pra B: o de B cai como `not-my-profile` e o de A nunca mais saía
  // (sonda W2 do review, 29/09/2026).
  const key = (p: PendingTombstone): string => JSON.stringify([p.kind, p.localId, p.owner])
  const newer = new Map(b.filter((p) => p.kind === 'series_progress').map((p) => [key(p), p]))
  const seen = new Set<string>()
  const out: PendingTombstone[] = []
  for (const p0 of [...a, ...b]) {
    const k = key(p0)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(newer.get(k) ?? p0)
  }
  return out
}

/**
 * Tombstone resolvido: entregue ao outbox, sem cópia no servidor pra apagar, ou
 * vencido por um pendente MAIS NOVO do mesmo item no outbox (`superseded`: é a escrita
 * legítima posterior que vai, e o tombstone tem que perder pra ela).
 */
function isSettled(status: TombstoneStatus): boolean {
  return status === 'queued' || status === 'not-syncable' || status === 'superseded'
}

/** O catálogo de uma série, no mínimo que o recálculo do derivado usa. */
interface SeriesCatalog {
  name: string | null
  seasons: Record<string, ReadonlyArray<{ id: string; episode_num?: string; season?: number }>>
}

function toCatalog(p: { series?: { name?: unknown } | null; seasons?: unknown }): SeriesCatalog {
  const name = typeof p.series?.name === 'string' && p.series.name.length > 0 ? p.series.name : null
  const seasons =
    p.seasons && typeof p.seasons === 'object' ? (p.seasons as SeriesCatalog['seasons']) : {}
  return { name, seasons }
}

/**
 * Recalcula o `series_progress` de uma série a partir das posições que FICARAM (PURO).
 * `null` = nada assistido sobrou (vira tombstone). A regra é a do `recomputeSeriesProgress`
 * da ficha e do player, pelo MESMO núcleo (`buildSeriesProgress` + `seriesProgressEpisodes`
 * do local-db): antes esta tinha a sua, e já divergia (sonda PAR). Sem catálogo, o registro
 * sai SEM `next*` e com `allEpisodesWatched: false` (não dá pra saber qual é o último
 * episódio), e a ficha completa ao abrir.
 */
export function deriveSeriesProgress(args: {
  id: string
  scope: string
  seriesId: string
  remaining: ReadonlyArray<Pick<PlaybackPosition, 'episodeId' | 'finished' | 'lastWatchedAt' | 'title' | 'poster'>>
  previous: SeriesProgress | undefined
  catalog: SeriesCatalog | undefined
}): SeriesProgress | null {
  if (args.remaining.length === 0) return null
  return buildSeriesProgress({
    id: args.id,
    providerScope: args.scope,
    seriesId: args.seriesId,
    title: args.previous?.title || args.catalog?.name || '',
    poster: args.previous?.poster ?? args.remaining.find((p) => p.poster)?.poster ?? null,
    episodes: args.catalog ? seriesProgressEpisodes(args.catalog.seasons) : [],
    positions: args.remaining,
    totalWithoutCatalog: args.previous?.totalEpisodesAtTime ?? 0,
  })
}

/** O teto global (ver STAMP_GIVE_UP_*): `boots` NÃO concluídos, `elapsed` da janela. */
function reachedGiveUp(boots: number, elapsedMs: number): boolean {
  return (
    boots >= STAMP_GIVE_UP_HARD_BOOTS || (boots >= STAMP_GIVE_UP_MIN_BOOTS && elapsedMs >= STAMP_GIVE_UP_MIN_MS)
  )
}

/** O estado a gravar, SEMPRE com os contadores enquanto a limpeza local não fechou
 *  (o passo 1 sem eles zerava o teto em todo boot que não chegava ao fim). */
function snapshot(
  localDone: boolean,
  pending: PendingTombstone[],
  tieTries: Record<string, number>,
  unfinished: { since: number; boots: number } | null,
  recalc: ReadonlyArray<{ scope: string; seriesId: string }>,
): MigrationState {
  return {
    v: 1,
    localDone,
    pending,
    ...(!localDone && Object.keys(tieTries).length > 0 ? { tieTries } : {}),
    ...(!localDone && unfinished !== null ? { unfinished } : {}),
    ...(!localDone && recalc.length > 0 ? { recalc: [...recalc] } : {}),
  }
}

/** O pendente leva o relógio da VARREDURA (o derivado), não o do carimbo? Por
 *  construção, só o `series_progress` (ver "O relógio do derivado"). */
function carriesScanClock(p: PendingTombstone): boolean {
  return p.kind === 'series_progress'
}

/** A série de um id de `series_progress` (`<scope>::series-progress:<id>`). */
function seriesOfProgressId(localId: string): { scope: string; seriesId: string } | null {
  const mark = '::series-progress:'
  const i = localId.lastIndexOf(mark)
  if (i <= 0) return null
  const seriesId = localId.slice(i + mark.length)
  return seriesId.length > 0 ? { scope: localId.slice(0, i), seriesId } : null
}

function tieKey(providerScope: string, seriesId: string): string {
  return `${providerScope}|${seriesId}`
}

/** O provedor de um scope (`p:HXTV::pf:abc` → `p:HXTV`). */
function providerOfScope(providerScope: string): string {
  const i = providerScope.indexOf('::')
  return i < 0 ? providerScope : providerScope.slice(0, i)
}

/**
 * O catálogo das séries que ainda não têm, do cache LOCAL da ficha (`SeriesInfoStore`,
 * sem rede) e só do provedor ativo (é dele o cache). Pro recálculo do derivado das séries
 * que não passaram pela busca do empate. Nunca lança.
 */
async function fillLocalCatalogs(
  series: Iterable<{ scope: string; seriesId: string }>,
  catalogs: Map<string, SeriesCatalog>,
): Promise<void> {
  const active = providerPart()
  for (const sr of series) {
    const key = tieKey(sr.scope, sr.seriesId)
    if (catalogs.has(key) || active === null || providerOfScope(sr.scope) !== active) continue
    try {
      const cached = await seriesInfoStore.get(sr.seriesId)
      if (cached?.payload) catalogs.set(key, toCatalog(cached.payload))
    } catch {
      // Sem cache: o derivado sai sem ponteiro (a ficha completa ao abrir).
    }
  }
}

/**
 * Durações reais dos episódios das séries com lote, só do provedor ativo. Primeiro o
 * `SeriesInfoStore` local, pra TODO lote (sem rede). Depois a API, só pros empates
 * PENDENTES que o cache não resolveu, com prioridade baixa. Falha de rede: a série
 * fica fora do mapa (o empate segue pendente). Resposta "não dá" (sem durações, 4xx,
 * provedor inativo): conta em `tieTries` e, na N-ésima, entra como mapa VAZIO (fecha
 * conservador). Nunca lança.
 */
async function fetchTieDurations(
  groups: ReadonlyArray<StampGroup>,
  tieTries: Record<string, number>,
  /** Saída: o catálogo de cada série que veio (cache local ou API), com ou sem duração. */
  catalogs: Map<string, SeriesCatalog>,
): Promise<Map<string, EpisodeDurations>> {
  const out = new Map<string, EpisodeDurations>()
  const active = providerPart()
  const seen = new Set<string>()
  // Resposta "não dá": conta um boot; no teto, o empate fecha conservador (mapa vazio).
  const tryFailed = (key: string): void => {
    const n = (tieTries[key] ?? 0) + 1
    tieTries[key] = n
    if (n >= STAMP_TIE_MAX_TRIES) out.set(key, new Map())
  }
  for (const g of groups) {
    const key = tieKey(g.providerScope, g.seriesId)
    if (seen.has(key)) continue
    seen.add(key)
    if (active === null || providerOfScope(g.providerScope) !== active) {
      // Sem a sessão do provedor do empate, não há a quem perguntar: conta.
      if (g.tie === 'pending') tryFailed(key)
      continue
    }
    // 1. O cache local da ficha (sem rede).
    let cached: Awaited<ReturnType<typeof seriesInfoStore.get>> = null
    try {
      cached = await seriesInfoStore.get(g.seriesId)
    } catch {
      cached = null
    }
    if (cached?.payload) catalogs.set(key, toCatalog(cached.payload))
    const local = episodeDurationsFrom(cached?.payload.seasons)
    if (local.size > 0) {
      out.set(key, local)
      continue
    }
    // 2. A API, só pro empate que depende disso.
    if (g.tie !== 'pending') continue
    let info: Awaited<ReturnType<typeof api.getSeriesInfo>>
    try {
      info = await runLowPriority(() => api.getSeriesInfo(g.seriesId))
    } catch (err) {
      // 4xx é RESPOSTA (a série não existe mais, ou o servidor recusa): conta. Rede,
      // 5xx e 408/429 não: tenta no próximo boot.
      if (isAnsweredRefusal(err)) tryFailed(key)
      continue
    }
    if (info) catalogs.set(key, toCatalog(info))
    const d = episodeDurationsFrom(info?.seasons)
    if (d.size > 0) {
      out.set(key, d)
      continue
    }
    tryFailed(key)
  }
  return out
}

/** O erro é uma recusa RESPONDIDA pelo servidor (4xx, exceto 408/429)? Lê o `status`
 *  do `ApiError` sem importar a classe. */
function isAnsweredRefusal(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429
}

/**
 * Relógio do tombstone: fim da rajada (`lastWatchedAt`, em SEGUNDOS) + a folga, em
 * ISO (o mesmo formato que o outbox compara como string e que o servidor grava em
 * `updated_at`). Esquecer o `* 1000` dá uma data de 1970 e o tombstone perde calado
 * no LWW de todos os carimbos.
 */
export function anchorIso(burstEndSec: number): string {
  return new Date(burstEndSec * 1000 + TOMBSTONE_ANCHOR_MARGIN_MS).toISOString()
}

/**
 * Roda a migração (uma rodada). Seguro chamar a cada boot: depois de concluída,
 * é um read de localStorage e retorna.
 */
export async function runAutoAdvanceStampMigration(): Promise<StampMigrationResult> {
  const state = readStampMigrationState()
  const result: StampMigrationResult = {
    skipped: false,
    pendingTies: 0,
    gaveUp: false,
    scanComplete: true,
    clockBehind: false,
    groups: [],
    removedPositions: [],
    removedSeriesProgress: [],
    rewrittenSeriesProgress: [],
    localOnlySeriesProgress: [],
    tombstones: [],
    state,
  }
  if (state.localDone && state.pending.length === 0) {
    result.skipped = true
    return result
  }

  let pending = state.pending
  let localDone = state.localDone
  let unfinished: { since: number; boots: number } | null = state.unfinished ?? null
  const tieTries: Record<string, number> = { ...(state.tieTries ?? {}) }
  let recalc: Array<{ scope: string; seriesId: string }> = [...(state.recalc ?? [])]

  const now = Date.now()
  // Relógio ANTES do piso (TV sem RTC antes do NTP): a fase local não roda neste boot.
  // Não conta boot, não abre a janela de dias, não varre, nenhum derivado nasce com ele.
  // O boot seguinte (com o relógio certo) faz tudo, e recalcula.
  result.clockBehind = now < STAMP_MIGRATION_EPOCH_MS
  const runLocal = !result.clockBehind

  // Teto GLOBAL, parte 1 (ver o cabeçalho): este boot conta ANTES do trabalho.
  let bootNo = 0
  let windowStart = now
  if (!localDone && runLocal) {
    const prev = unfinished
    // Janela de dias: recomeça se não há início ou se o relógio voltou pra antes dele. (O
    // início nunca é de relógio errado: com ele a fase local não roda, e não grava.)
    windowStart = prev !== null && prev.since <= now ? prev.since : now
    const bootsSoFar = prev?.boots ?? 0
    if (reachedGiveUp(bootsSoFar, now - windowStart)) {
      // Os boots anteriores chegaram ao teto sem concluir (penduraram no meio): fecha
      // conservador sem refazer o trabalho.
      localDone = true
      unfinished = null
      recalc = []
      result.gaveUp = true
    } else {
      bootNo = bootsSoFar + 1
      unfinished = { since: windowStart, boots: bootNo }
      writeState(snapshot(false, pending, tieTries, unfinished, recalc)) // write-ahead
    }
  }

  if (!localDone && runLocal) {
    // O primeiro pull da sessão antes da varredura: o que outro aparelho escreveu
    // depois do último pull da TV chega antes, e não é apagado como carimbo.
    if (getSwipeUserId() !== null) {
      try {
        await syncPull()
      } catch {
        // Best-effort: o relógio ancorado do tombstone é a defesa que não depende disto.
      }
    }
    let records
    try {
      records = await readAllPositionsAllScopes()
    } catch {
      // IDB ilegível agora: limpa o que der no fallback e tenta o resto no próximo boot.
      result.scanComplete = false
      records = readFallbackPositionsAllScopes()
    }
    // 1ª passada sem durações: acha os empates que dependem delas.
    let groups = findAutoAdvanceStamps(records)
    const catalogs = new Map<string, SeriesCatalog>()
    const durations = await fetchTieDurations(groups, tieTries, catalogs)
    if (durations.size > 0) groups = findAutoAdvanceStamps(records, (sc, se) => durations.get(tieKey(sc, se)))
    result.groups = groups
    result.pendingTies = groups.filter((g) => g.tie === 'pending').length
    const owner = getSwipeUserId()
    const newPending: PendingTombstone[] = []
    const touched = new Map<string, { scope: string; seriesId: string }>()
    for (const g of groups) {
      if (g.removeIds.length === 0) continue // empate pendente sem carimbo nos segundos seguintes
      const updatedAt = anchorIso(g.burstEndSec)
      for (const localId of g.removeIds) newPending.push({ localId, kind: 'position', owner, updatedAt })
      touched.set(tieKey(g.providerScope, g.seriesId), { scope: g.providerScope, seriesId: g.seriesId })
    }
    // O derivado cujo pendente saiu com o relógio adiantado volta aqui pra ser recalculado
    // (ver "O relógio do derivado"), mesmo sem carimbo nenhum mais na série.
    for (const r of recalc) touched.set(tieKey(r.scope, r.seriesId), r)
    await fillLocalCatalogs(touched.values(), catalogs)
    const positionIds = groups.flatMap((g) => g.removeIds)
    // O `series_progress` das séries tocadas: RECALCULADO das posições que ficam (ver o
    // cabeçalho), escrito com o relógio de AGORA; tombstone (agora) só se nada ficou.
    const removedSet = new Set(positionIds)
    const progressIds = [...touched.values()].map((t) => seriesProgressLocalId(t.scope, t.seriesId))
    const previous = await readSeriesProgressLocal(progressIds)
    // O derivado MAIS NOVO que as posições que sobraram (ver "O derivado de posições que
    // OUTRO aparelho apagou"): só com a varredura completa (com parte das posições, o
    // "maior finished" sairia menor e qualquer snapshot pareceria contaminado).
    const stale: SeriesProgress[] = []
    let staleReadFailed = false
    if (result.scanComplete) {
      let all: SeriesProgress[] = []
      try {
        all = await readAllSeriesProgressAllScopes()
      } catch {
        staleReadFailed = true
      }
      const maxFinished = new Map<string, number>()
      for (const r of records) {
        if (r.type !== 'series-episode' || !r.seriesId || !r.finished || removedSet.has(r.id)) continue
        const k = tieKey(r.providerScope, r.seriesId)
        maxFinished.set(k, Math.max(maxFinished.get(k) ?? 0, r.lastWatchedAt))
      }
      for (const sp of all) {
        const k = tieKey(sp.providerScope, sp.seriesId)
        if (touched.has(k)) continue // já recalculado acima, e com escrita pro servidor
        if (sp.lastWatchedAt > (maxFinished.get(k) ?? 0)) stale.push(sp)
      }
      await fillLocalCatalogs(
        stale.map((sp) => ({ scope: sp.providerScope, seriesId: sp.seriesId })),
        catalogs,
      )
    }
    const staleWrites: SeriesProgress[] = []
    const staleDeletes: string[] = []
    for (const sp of stale) {
      const derived = deriveSeriesProgress({
        id: sp.id,
        scope: sp.providerScope,
        seriesId: sp.seriesId,
        remaining: records.filter(
          (r) => r.providerScope === sp.providerScope && r.type === 'series-episode' && r.seriesId === sp.seriesId,
        ),
        previous: sp,
        catalog: catalogs.get(tieKey(sp.providerScope, sp.seriesId)),
      })
      if (derived === null) staleDeletes.push(sp.id)
      else staleWrites.push(derived)
    }
    const nowIso = new Date(now).toISOString()
    const progressWrites: SeriesProgress[] = []
    const progressDeletes: string[] = []
    for (const [key, t] of touched) {
      const id = seriesProgressLocalId(t.scope, t.seriesId)
      const remaining = records.filter(
        (r) =>
          r.providerScope === t.scope &&
          r.type === 'series-episode' &&
          r.seriesId === t.seriesId &&
          !removedSet.has(r.id),
      )
      const derived = deriveSeriesProgress({
        id,
        scope: t.scope,
        seriesId: t.seriesId,
        remaining,
        previous: previous.get(id),
        catalog: catalogs.get(key),
      })
      if (derived === null) {
        progressDeletes.push(id)
        newPending.push({ localId: id, kind: 'series_progress', owner, updatedAt: nowIso })
      } else {
        progressWrites.push(derived)
        newPending.push({ localId: id, kind: 'series_progress', owner, updatedAt: nowIso, payload: derived })
      }
    }

    // 1. Registra o que o servidor precisa saber ANTES de apagar.
    pending = mergePending(pending, newPending)
    writeState(snapshot(false, pending, tieTries, unfinished, recalc))

    // 2. Apaga local (sem sync — os tombstones saem no passo 3, com desfecho).
    const pos = await deletePositionsLocalOnly(positionIds)
    const prog = await deleteSeriesProgressLocalOnly(progressDeletes)
    const rewriteFailed: string[] = []
    for (const rec of progressWrites) if (!(await putSeriesProgressLocalOnly(rec))) rewriteFailed.push(rec.id)
    // O recálculo só local (sem pendente: o servidor já está certo).
    const staleDel = await deleteSeriesProgressLocalOnly(staleDeletes)
    const staleFailed = [...staleDel.failed]
    for (const rec of staleWrites) if (!(await putSeriesProgressLocalOnly(rec))) staleFailed.push(rec.id)
    result.removedPositions = positionIds.filter((id) => !pos.failed.includes(id))
    result.removedSeriesProgress = progressDeletes.filter((id) => !prog.failed.includes(id))
    result.rewrittenSeriesProgress = progressWrites.map((r) => r.id).filter((id) => !rewriteFailed.includes(id))
    result.localOnlySeriesProgress = [...staleWrites.map((r) => r.id), ...staleDeletes].filter(
      (id) => !staleFailed.includes(id),
    )
    // Empate pendente: não fecha. O próximo boot varre de novo e tenta de novo.
    localDone =
      result.scanComplete &&
      pos.failed.length === 0 &&
      prog.failed.length === 0 &&
      rewriteFailed.length === 0 &&
      !staleReadFailed &&
      staleFailed.length === 0 &&
      result.pendingTies === 0
    if (localDone) {
      unfinished = null
      recalc = []
    } else if (reachedGiveUp(bootNo, now - windowStart)) {
      // Teto GLOBAL, parte 2: este boot (já contado) chegou ao teto sem concluir.
      localDone = true
      unfinished = null
      recalc = []
      result.gaveUp = true
    }
  }

  // 3. Tombstones: só pro DONO, só pros perfis da conta dele, e só saem da lista
  //    quando o outbox aceitou.
  const current = getSwipeUserId()
  const mine = pending.filter((p) => p.owner !== null && p.owner === current)
  let ownedProfiles: Set<string> | null = null
  if (mine.length > 0 && current !== null) {
    // Com o token DESTA conta (AP-116): se a sessão trocar no meio, a lista não é a de
    // outra conta, é nenhuma.
    const profiles = await listRemoteProfiles(undefined, { asUser: current })
    if (profiles !== null) ownedProfiles = new Set(profiles.filter((p) => !p.deletedAt).map((p) => p.id))
  }
  const stillPending: PendingTombstone[] = []
  const sendNow = Date.now()
  for (const p of pending) {
    let outcome: TombstoneOutcome
    if (p.owner === null) {
      outcome = 'no-owner'
    } else if (p.owner !== current) {
      outcome = 'not-owner'
      stillPending.push(p)
    } else if (ownedProfiles === null) {
      outcome = 'profiles-unavailable'
      stillPending.push(p)
    } else if (!ownedProfiles.has(decomposeLocalId(p.localId)?.profileId ?? '')) {
      outcome = 'not-my-profile'
    } else if (carriesScanClock(p) && sendNow < STAMP_MIGRATION_EPOCH_MS) {
      // O relógio de AGORA é o errado: não dá pra julgar o do pendente. Espera.
      outcome = 'clock-behind'
      stillPending.push(p)
    } else if (carriesScanClock(p) && !isAcceptableClock(p.updatedAt, sendNow)) {
      // O relógio do PENDENTE é o errado (à frente do de agora): nunca sai com ele. A
      // série volta pra varredura seguinte, que recalcula com o relógio certo.
      outcome = 'clock-ahead'
      const series = seriesOfProgressId(p.localId)
      if (series !== null && !recalc.some((r) => r.scope === series.scope && r.seriesId === series.seriesId)) {
        recalc.push(series)
      }
      localDone = false
    } else {
      try {
        outcome =
          p.payload !== undefined
            ? await enqueueUpsert({
                localId: p.localId,
                kind: p.kind,
                payload: p.payload,
                updatedAt: p.updatedAt,
                origin: 'stamp-migration',
                // A decisão pelo dono (acima) vale até o enqueue: trocou a conta no
                // meio, não enfileira (`no-session`, fica pendente com o dono dele).
                asUser: p.owner,
              })
            : await enqueueDelete({
                localId: p.localId,
                kind: p.kind,
                updatedAt: p.updatedAt,
                origin: 'stamp-migration',
                asUser: p.owner,
              })
      } catch {
        outcome = 'failed'
      }
      if (!isSettled(outcome)) stillPending.push(p)
    }
    result.tombstones.push({ ...p, outcome })
  }

  const next: MigrationState = snapshot(localDone, stillPending, tieTries, unfinished, recalc)
  writeState(next)
  result.state = next
  reportRound(result)
  return result
}

/** Telemetria da rodada (contagens, nunca ids): o desfecho no aparelho. O do servidor
 *  sai do flusher (`stamp_migration_push`). */
function reportRound(r: StampMigrationResult): void {
  const tombstones: Record<string, number> = {}
  for (const t of r.tombstones) tombstones[t.outcome] = (tombstones[t.outcome] ?? 0) + 1
  try {
    track({
      type: 'stamp_migration',
      ts: Math.floor(Date.now() / 1000),
      scanComplete: r.scanComplete,
      clockBehind: r.clockBehind,
      groups: r.groups.length,
      pendingTies: r.pendingTies,
      gaveUp: r.gaveUp,
      removedPositions: r.removedPositions.length,
      removedSeriesProgress: r.removedSeriesProgress.length,
      rewrittenSeriesProgress: r.rewrittenSeriesProgress.length,
      localOnlySeriesProgress: r.localOnlySeriesProgress.length,
      tombstones,
    })
  } catch {
    // Telemetria nunca derruba a migração.
  }
}
